import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	DynamicPagesErrorCallbacksAPI,
	DynamicPagesErrorInfo,
	DynamicPagesNavigationErrorCallback,
	DynamicPagesSiteConfig,
	IDynamicPagesResponseHandler,
} from '../types'
import { SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { name } from '../symbols'
import { CandidateRouteInfo } from 'feature-router'
import {
	getErrorPageId,
	getRouterPrefix,
	getRouterSuffix,
	getSuccessResponse,
	isExternalUrl,
	getRelativeUrl,
} from '../utils'

const getErrorInfo = (
	statusCode: number,
	routeInfo: Partial<CandidateRouteInfo>,
	externalBaseUrl: string
): DynamicPagesErrorInfo => {
	const routerPrefix = getRouterPrefix(routeInfo.relativeUrl!)
	const routerSuffix = getRouterSuffix(routeInfo.relativeEncodedUrl!)

	return {
		statusCode,
		routerUrl: `/${routerPrefix}${routerSuffix}`,
		publicUrl: `${externalBaseUrl}/${routerPrefix}${routerSuffix}`,
	}
}

export const DsDynamicPagesResponseHandler = withDependencies(
	[named(SiteFeatureConfigSymbol, name)],
	({ externalBaseUrl }: DynamicPagesSiteConfig): IDynamicPagesResponseHandler & DynamicPagesErrorCallbacksAPI => {
		const previewErrorCallbacks: Array<DynamicPagesNavigationErrorCallback> = []

		const callPreviewErrorCallbacks = (errorInfo: DynamicPagesErrorInfo) => {
			previewErrorCallbacks.forEach((callback) => callback(errorInfo))
		}

		return {
			registerToDynamicPagesNavigationError(callback) {
				previewErrorCallbacks.push(callback)
			},
			async handleResponse(routerResponse, routeInfo) {
				return routerResponse
					.then(async (response: Response) => {
						if (!response.ok) {
							throw response
						}
						const data = await response.json()
						const { result, exception } = data
						const { redirectUrl, status } = result

						if (redirectUrl) {
							if (isExternalUrl(redirectUrl)) {
								return null
							}
							const routerPrefix = getRouterPrefix(routeInfo.relativeUrl!)
							const relativeUrl = getRelativeUrl(redirectUrl, routerPrefix)
							return {
								redirectUrl: relativeUrl,
							}
						}

						const errorPageId = getErrorPageId(result, exception)

						if (errorPageId) {
							const statusCode = exception ? 500 : status

							callPreviewErrorCallbacks(getErrorInfo(statusCode, routeInfo, externalBaseUrl))
							return null
						}

						return getSuccessResponse(routeInfo, data)
					})
					.catch(() => {
						callPreviewErrorCallbacks(getErrorInfo(500, routeInfo, externalBaseUrl))
						return null
					})
			},
		}
	}
)
